///////////////////////////////////////////////
//           Match data interfaces           //
///////////////////////////////////////////////

import type { Translatable, Translation, Translations } from '../utils';

import type { CustomLocale, Enum, LANGUAGES } from '.';

export interface Match extends Translatable {
  awayTeam?: Team;
  behindClosedDoors?: boolean;
  competition: Competition;
  condition?: Condition;
  fouls?: TeamGroupOfint;
  fullTimeAt?: string;
  group?: Group;
  homeTeam?: Team;
  id: string;
  kickOffTime?: KickOffTime;
  leg?: Leg;
  lineupStatus: LineupStatus;
  matchAttendance?: number;
  matchday?: Matchday;
  matchNumber?: number;
  minute?: { injury?: number; normal: number };
  phase?: MatchPhase;
  playerEvents?: PlayerEventsWrapper;
  referees?: MatchPerson[];
  relatedMatches?: RelatedMatch[];
  round?: Round;
  score?: Score;
  seasonYear: string;
  sessionNumber: number;
  stadium?: Stadium;
  status: MatchStatus;
  translations?: { phaseName: Translation };
  type?: MatchType;
  winner?: WinnerWrapper;
  streamUrl?: {
    [lang: string]: string;
  };
}

export interface Team extends Translatable {
  associationId?: string;
  associationLogoUrl?: string;
  bigLogoUrl?: string;
  confederationType?: string;
  countryCode?: string;
  history?: TeamHistory[];
  id: string;
  idProvider?: string;
  internationalName?: string;
  isPlaceHolder?: boolean;
  logoUrl?: string;
  mediumLogoUrl?: string;
  organizationId?: string;
  teamCode?: string;
  teamTypeDetail?: string;
  translations?: TeamTranslations;
  typeIsNational?: boolean;
  typeTeam?: TeamType;
}

export interface TeamHistory extends Translatable {
  bigLogoUrl?: string;
  countryCode?: string;
  countryTranslationResourceKey?: string;
  fromDate?: string;
  fromPhase?: string[];
  fromYear?: number;
  inheritTeamId?: string;
  internationalName?: string;
  logoUrl?: string;
  mediumLogoUrl?: string;
  teamCode?: string;
  teamId: string;
  toDate?: string;
  toPhase?: string[];
  toYear?: number;
  translations?: TeamTranslations;
}

export interface TeamTranslations extends Translations {
  countryName?: Translation;
  displayName?: Translation;
  displayOfficialName?: Translation;
  displayTeamCode?: Translation;
}

export interface Competition extends Translatable {
  age?: string;
  code?: string;
  countryCode?: string;
  id: string;
  images?: { [key: string]: string };
  metaData?: { name: string; phase: string };
  region: string;
  sex?: string;
  sportsType?: CompetitionSportsType;
  teamCategory?: TeamCategory;
  translations?: CompetitionTranslations;
  type?: string;
}

export interface CompetitionTranslations extends Translations {
  countryName?: Translation;
  name?: Translation;
  qualifyingName?: Translation;
  tournamentName?: Translation;
}

export interface Condition extends Translatable {
  humidity?: number;
  pitchCondition?: string;
  temperature?: number;
  translations?: {
    pitchConditionName?: Translation;
    weatherConditionName?: Translation;
  };
  weatherCondition?: string;
  windSpeed?: number;
}

export interface TeamGroupOfint {
  away: number;
  home: number;
}

export interface Group extends Translatable {
  competitionId?: string;
  id: string;
  league?: League;
  metaData?: { groupName?: string; groupShortName?: string };
  order?: number;
  phase?: string;
  roundId?: string;
  seasonYear?: string;
  translations?: LeagueTranslations;
  type?: string;
}

export interface League extends Translatable {
  id: string;
  metaData?: { leagueName?: string; leagueShortName?: string };
  order?: number;
  roundId?: string;
  translations?: LeagueTranslations;
}

export interface LeagueTranslations extends Translations {
  name?: Translation;
  shortName?: Translation;
}

export interface KickOffTime {
  date: string;
  dateTime?: string;
  utcOffsetInHours?: number;
}

export interface Leg extends Translatable {
  dateTimeFrom?: string;
  dateTimeTo?: string;
  number: number;
  translations?: { name: Translation };
}

export interface Matchday extends Translatable {
  competitionId: string;
  dateFrom?: string;
  dateTo?: string;
  id: string;
  longName?: string;
  name: string;
  roundId: string;
  seasonYear: string;
  sequenceNumber?: string;
  status?: string;
  translations?: MatchdayTranslations;
  type?: MatchDayType;
}

export interface MatchdayTranslations extends Translations {
  longName?: Translation;
  name?: Translation;
}

export interface PlayerEventsWrapper {
  penaltiesMissed?: PlayerEvent[];
  penaltyScorers?: PlayerEvent[];
  redCards?: PlayerEvent[];
  scorers?: PlayerEvent[];
}

export interface PlayerEvent {
  goalType?: PlayerEventSubType;
  id: string;
  images?: { [key: string]: string };
  penaltyType?: PlayerEventSubType;
  player?: Player;
  teamId?: string;
  teamIdProvider?: string;
  time?: { injuryMinute?: number; minute: number; second?: number };
  totalScore?: TeamGroupOfint;
}

export interface Player extends Translatable {
  age?: string;
  birthDate?: string;
  clubId?: string;
  clubShirtName?: string;
  countryCode?: string;
  countryOfBirthCode?: string;
  detailedFieldPosition?: string;
  fieldPosition?: string;
  gender?: 'MALE' | 'FEMALE';
  height?: number;
  id: string;
  idProvider?: string;
  imageUrl?: string;
  internationalName?: string;
  nationalFieldPosition?: string;
  nationalShirtName?: string;
  nationalTeamId?: string;
  translations?: PlayerTranslations;
  weight?: number;
}

export interface PlayerTranslations extends Translations {
  countryName?: Translation;
  countryOfBirthName?: Translation;
  fieldPosition?: Translation;
  firstName?: Translation;
  lastName?: Translation;
  name?: Translation;
  nationalFieldPosition?: Translation;
  shortName?: Translation;
}

export interface MatchPerson extends Translatable {
  images?: { [key: string]: string };
  imageUrl?: string;
  person: Person;
  role: MatchPersonRole;
  translations?: PersonTranslations;
}

export interface Person extends Translatable {
  countryCode?: string;
  gender?: 'MALE' | 'FEMALE';
  id: string;
  translations?: PlayerTranslations;
}

export interface PersonTranslations extends Translations {
  roleName: Translation;
}

export interface RelatedMatch {
  awayTeam?: Team;
  homeTeam?: Team;
  id: string;
  kickOffTime?: KickOffTime;
  score?: Score;
  status?: string;
  type?: string;
  winner?: WinnerWrapper;
}

export interface Score {
  aggregate?: TeamGroupOfint;
  penalty?: TeamGroupOfint;
  regular?: TeamGroupOfint;
  total: TeamGroupOfint;
}

export interface WinnerWrapper {
  aggregate?: Winner;
  match: Winner;
}

export interface Winner extends Translatable {
  reason?: WinnerReason;
  team?: Team;
  translations?: WinnerTranslations;
}

export interface WinnerTranslations extends Translations {
  reasonText?: Translation;
  reasonTextAbbr?: Translation;
}

export interface Round extends Translatable {
  active: boolean;
  competitionId: string;
  dateFrom?: string;
  dateTo?: string;
  groupCount?: number;
  id: string;
  metaData?: RoundMetaData;
  mode: RoundMode;
  modeDetail?: string;
  orderInCompetition: number;
  phase: RoundPhase;
  seasonYear: string;
  secondaryType?: string;
  stadiumNameType?: string;
  status?: string;
  teamCount?: number;
  translations?: RoundTranslations;
}

export interface RoundMetaData {
  name?: string;
  type?: string;
}

export interface RoundTranslations extends Translations {
  abbreviation?: Translation;
  name?: Translation;
  shortName?: Translation;
}

export interface MatchTranslations extends Translations {
  phaseName: Translation;
}

export interface Stadium extends Translatable {
  address?: string;
  capacity?: number;
  city?: City;
  countryCode?: string;
  geolocation?: Geolocation;
  id: string;
  images?: { [key: string]: string };
  openingDate?: string;
  translations?: StadiumTranslations;
}

export interface City extends Translatable {
  countryCode?: string;
  id: string;
  translations?: { name: Translation };
}

export interface Geolocation {
  latitude: number;
  longitude: number;
}

export interface StadiumTranslations extends Translations {
  mediaName?: Translation;
  name?: Translation;
  officialName?: Translation;
  specialEventsName?: Translation;
  sponsorName?: Translation;
}

export interface MatchCssVariant {
  size: MatchUnitSize;
}

export interface MatchWording {
  '1stleg'?: string;
  'line-ups'?: string;
  'single-leg-match'?: string;
  abandoned?: string;
  aggregate_abbr?: string;
  canceled?: string;
  extra_time_short?: string;
  full_time?: string;
  ft?: string;
  live?: string;
  watch_live?: string;
  mu_goal?: string;
  owngoal_abbr?: string;
  penalty_abbr?: string;
  postponed?: string;
  suspended?: string;
  unknown?: string;
  upcoming?: string;
  futsal_foulcounter?: string;
  tbc?: string;
  see_more?: string;
  view_details?: string;
  watch?: string;
}

export interface MatchCalendarFormat {
  sameDay?: string;
  nextDay?: string;
  nextWeek?: string;
  lastDay?: string;
  lastWeek?: string;
  sameElse?: string;
}

export interface MatchStoreState {
  dateCalendarFormat: MatchCalendarFormat;
  disableScorerLink: boolean;
  disableTeamsLink: boolean;
  isCtaSlotEmpty: boolean;
  isHighlightCtaSlotEmpty: boolean;
  language: LANGUAGES;
  locale: CustomLocale | string;
  matchData: Match;
  playerServiceUrl: string;
  size: MatchUnitSize;
  teamServiceUrl: string;
  wording: MatchWording;
  hideDate: boolean;
  hideRound: boolean;
  hideContext: boolean;
  hideCta: boolean;
  shownInBrackets?: boolean;
  showRedCards: boolean;
  showSeason: boolean;
  showLocation: boolean;
  showFouls: boolean;
  useTeamCode: boolean;
  goalScored?: boolean;
  customTimeZone?: string;
  showHighlightUrl: boolean;
  ctaAriaLabel: string;
}

export const CompetitionSportsType = {
  FOOTBALL: 'FOOTBALL',
  FUTSAL: 'FUTSAL',
} as const;
export type CompetitionSportsType = Enum<typeof CompetitionSportsType>;

export const EventType = {
  ASSIST: 'ASSIST',
  ATTACK: 'ATTACK',
  CHANGE_PHASE: 'CHANGE_PHASE',
  CORNER: 'CORNER',
  DISALLOWED_GOAL: 'DISALLOWED_GOAL',
  DROPPED_BALL: 'DROPPED_BALL',
  END_PHASE: 'END_PHASE',
  EXTRA_TIME: 'EXTRA_TIME',
  FAST_ACTION: 'FAST_ACTION',
  FAST_ATTACK: 'FAST_ATTACK',
  FOUL: 'FOUL',
  FREE_KICK: 'FREE_KICK',
  GOAL: 'GOAL',
  GOAL_OPPORTUNITY: 'GOAL_OPPORTUNITY',
  INJURY_TIME: 'INJURY_TIME',
  KICK_OFF: 'KICK_OFF',
  LOST_BALL: 'LOST_BALL',
  OFFSIDE: 'OFFSIDE',
  ORGANIZED_ATTACK: 'ORGANIZED_ATTACK',
  PASS: 'PASS',
  PAUSE: 'PAUSE',
  PENALTY: 'PENALTY',
  RECOVERED_BALL: 'RECOVERED_BALL',
  RED_CARD: 'RED_CARD',
  RED_YELLOW_CARD: 'RED_YELLOW_CARD',
  RESUME: 'RESUME',
  SAVE: 'SAVE',
  SHOT_BLOCKED: 'SHOT_BLOCKED',
  SHOT_ON_GOAL: 'SHOT_ON_GOAL',
  SHOT_WIDE: 'SHOT_WIDE',
  SOLO_RUN: 'SOLO_RUN',
  START_PHASE: 'START_PHASE',
  SUBSTITUTION: 'SUBSTITUTION',
  TACKLE: 'TACKLE',
  THROW_IN: 'THROW_IN',
  VAR: 'VAR',
  VAR_CHECK: 'VAR_CHECK',
  WARNING: 'WARNING',
  YELLOW_CARD: 'YELLOW_CARD',
} as const;
export type EventType = Enum<typeof EventType>;

export const EventSubType = {
  PENALTY_SECOND_MARK: 'PENALTY_SECOND_MARK',
  OWN: 'OWN',
  PENALTY: 'PENALTY',
  MISSED: 'MISSED',
  SCORED: 'SCORED',
  BAR: 'BAR',
  POST: 'POST',
  HANDBALL: 'HANDBALL',
  SIMULATION: 'SIMULATION',
  FULL_TIME: 'FULL_TIME',
  PAUSED: 'PAUSED',
  SUSPENDED: 'SUSPENDED',
  TIMEOUT: 'TIMEOUT',
  FOUL_PLAY: 'FOUL_PLAY',
  ROUGH_PLAY: 'ROUGH_PLAY',
  TRIP_OPPONENT: 'TRIP_OPPONENT',
  FOUL_PLAYROUGH_PLAY: 'FOUL_PLAYROUGH_PLAY',
  DANGEROUS_PLAY: 'DANGEROUS_PLAY',
  TIME_WASTING: 'TIME_WASTING',
  KICKING_BALL_AWAY: 'KICKING_BALL_AWAY',
  HOLDING_OPPONENT: 'HOLDING_OPPONENT',
  IMPROPER_CONDUCT: 'IMPROPER_CONDUCT',
  DISSENT: 'DISSENT',
  PUSHING_OPPONENT: 'PUSHING_OPPONENT',
  FREE_KICK_DISTANCE: 'FREE_KICK_DISTANCE',
  CHARGING_OPPONENT: 'CHARGING_OPPONENT',
  VIOLENT_PLAY: 'VIOLENT_PLAY',
  ENTERING_WITHOUT_PERMISSION: 'ENTERING_WITHOUT_PERMISSION',
  DIRECT_FREE_KICK: 'DIRECT_FREE_KICK',
  INDIRECT_FREE_KICK: 'INDIRECT_FREE_KICK',
  GOALKEEPER: 'GOALKEEPER',
  FREE_KICK: 'FREE_KICK',
  CORNER: 'CORNER',
  CROSS: 'CROSS',
  FAST_ATTACK: 'FAST_ATTACK',
  WOODWORK: 'WOODWORK',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CAUSING_GOAL: 'CAUSING_GOAL',
  HALF_TIME: 'HALF_TIME',
  SAVED: 'SAVED',
  CROSSBAR: 'CROSSBAR',
  WIDE: 'WIDE',
  GOAL_KICK: 'GOAL_KICK',
  LONG: 'LONG',
  MEDIUM: 'MEDIUM',
  CLEARANCE: 'CLEARANCE',
  SHORT: 'SHORT',
  LEFT: 'LEFT',
  RIGHT: 'RIGHT',
  CENTER: 'CENTER',
} as const;
export type EventSubType = (typeof EventSubType)[keyof typeof EventSubType];

export const HomeAwayTeam = {
  HOME: 'home',
  AWAY: 'away',
} as const;
export type HomeAwayTeam = Enum<typeof HomeAwayTeam>;

export const LineupStatus = {
  AVAILABLE: 'AVAILABLE',
  NOT_AVAILABLE: 'NOT_AVAILABLE',
  TACTICAL_AVAILABLE: 'TACTICAL_AVAILABLE',
} as const;
export type LineupStatus = Enum<typeof LineupStatus>;

export const MatchDayType = {
  FINAL: 'FINAL',
  FIRST_LEG: 'FIRST_LEG',
  MATCHDAY: 'MATCHDAY',
  SECOND_LEG: 'SECOND_LEG',
  UNKNOWN: 'UNKNOWN',
} as const;
export type MatchDayType = Enum<typeof MatchDayType>;

export const MatchPersonRole = {
  ADDITIONAL_ASSISTANT_REFEREE_ONE: 'ADDITIONAL_ASSISTANT_REFEREE_ONE',
  ADDITIONAL_ASSISTANT_REFEREE_TWO: 'ADDITIONAL_ASSISTANT_REFEREE_TWO',
  ASSISTANT_COACH: 'ASSISTANT_COACH',
  ASSISTANT_REFEREE_ONE: 'ASSISTANT_REFEREE_ONE',
  ASSISTANT_REFEREE_TWO: 'ASSISTANT_REFEREE_TWO',
  ASSISTANT_VIDEO_ASSISTANT_REFEREE: 'ASSISTANT_VIDEO_ASSISTANT_REFEREE',
  ASSISTANT_VIDEO_ASSISTANT_REFEREE_ONE: 'ASSISTANT_VIDEO_ASSISTANT_REFEREE_ONE',
  ASSISTANT_VIDEO_ASSISTANT_REFEREE_THREE: 'ASSISTANT_VIDEO_ASSISTANT_REFEREE_THREE',
  ASSISTANT_VIDEO_ASSISTANT_REFEREE_TWO: 'ASSISTANT_VIDEO_ASSISTANT_REFEREE_TWO',
  COACH: 'COACH',
  FOURTH_OFFICIAL: 'FOURTH_OFFICIAL',
  REFEREE: 'REFEREE',
  REFEREE_OBSERVER: 'REFEREE_OBSERVER',
  REFEREE_ONE: 'REFEREE_ONE',
  REFEREE_THREE: 'REFEREE_THREE',
  REFEREE_TWO: 'REFEREE_TWO',
  TIMEKEEPER: 'TIMEKEEPER',
  UEFA_DELEGATE: 'UEFA_DELEGATE',
  VIDEO_ASSISTANT_REFEREE: 'VIDEO_ASSISTANT_REFEREE',
} as const;
export type MatchPersonRole = Enum<typeof MatchPersonRole>;

export const MatchPhase = {
  END_EXTRA_TIME_SECOND_HALF: 'END_EXTRA_TIME_SECOND_HALF',
  END_SECOND_HALF: 'END_SECOND_HALF',
  EXTRA_TIME_BREAK: 'EXTRA_TIME_BREAK',
  EXTRA_TIME_FIRST_HALF: 'EXTRA_TIME_FIRST_HALF',
  EXTRA_TIME_SECOND_HALF: 'EXTRA_TIME_SECOND_HALF',
  FIRST_HALF: 'FIRST_HALF',
  FULL_TIME: 'FULL_TIME',
  HALF_TIME_BREAK: 'HALF_TIME_BREAK',
  PAUSE: 'PAUSE',
  PENALTY: 'PENALTY',
  PRE_MATCH: 'PRE_MATCH',
  SECOND_HALF: 'SECOND_HALF',
} as const;
export type MatchPhase = Enum<typeof MatchPhase>;

export const MatchStatus = {
  ABANDONED: 'ABANDONED',
  CANCELED: 'CANCELED',
  FINISHED: 'FINISHED',
  LIVE: 'LIVE',
  POSTPONED: 'POSTPONED',
  SUSPENDED: 'SUSPENDED',
  UNKNOWN: 'UNKNOWN',
  UPCOMING: 'UPCOMING',
} as const;
export type MatchStatus = Enum<typeof MatchStatus>;

export const MatchType = {
  FIRST_LEG: 'FIRST_LEG',
  GROUP_STAGE: 'GROUP_STAGE',
  REPLAY: 'REPLAY',
  SECOND_LEG: 'SECOND_LEG',
  SINGLE: 'SINGLE',
} as const;
export type MatchType = Enum<typeof MatchType>;

export const MatchWinner = {
  HOME: 'HOME',
  AWAY: 'AWAY',
  DRAW: 'DRAW',
} as const;
export type MatchWinner = Enum<typeof MatchWinner>;

export const PlayerEventSubType = {
  // PENALTIES
  MISSED: 'MISSED',
  // GOALS & PENALTIES
  SCORED: 'SCORED',
  // GOALS
  OWN: 'OWN',
  PENALTY: 'PENALTY',
  FULL_TIME: 'FULL_TIME',
  PENALTY_SECOND_MARK: 'PENALTY_SECOND_MARK',
  // Only for match service <v5
  // PENALTIES
  GOAL: 'GOAL',
  MISS: 'MISS',
  // GOALS
  OWN_GOAL: 'OWN_GOAL',
  PENALTY_GOAL: 'PENALTY_GOAL',
} as const;
export type PlayerEventSubType = Enum<typeof PlayerEventSubType>;

export const RoundMode = {
  FINAL: 'FINAL',
  GROUP: 'GROUP',
  KNOCK_OUT: 'KNOCK_OUT',
  OTHER: 'OTHER',
} as const;
export type RoundMode = Enum<typeof RoundMode>;

export const RoundPhase = {
  PREQUALIFYING: 'PREQUALIFYING',
  QUALIFYING: 'QUALIFYING',
  TOURNAMENT: 'TOURNAMENT',
} as const;
export type RoundPhase = Enum<typeof RoundPhase>;

export const RoundStatus = {
  CURRENT: 'CURRENT',
  FINISHED: 'FINISHED',
  UPCOMING: 'UPCOMING',
} as const;
export type RoundStatus = Enum<typeof RoundStatus>;

export const TeamType = {
  DOMESTIC: 'DOMESTIC',
  NATIONAL: 'NATIONAL',
  OTHER: 'OTHER',
  PLACEHOLDER: 'PLACEHOLDER',
} as const;
export type TeamType = Enum<typeof TeamType>;

export const TeamCategory = {
  NATIONAL: 'NATIONAL',
  CLUB: 'CLUB',
} as const;
export type TeamCategory = Enum<typeof TeamCategory>;

export const WinnerReason = {
  DRAW: 'DRAW',
  WIN_BY_FORFEIT: 'WIN_BY_FORFEIT',
  WIN_ON_AGGREGATE: 'WIN_ON_AGGREGATE',
  WIN_ON_AWAY_GOAL: 'WIN_ON_AWAY_GOAL',
  WIN_ON_EXTRA_TIME: 'WIN_ON_EXTRA_TIME',
  WIN_ON_GOLDEN_GOAL: 'WIN_ON_GOLDEN_GOAL',
  WIN_ON_PENALTIES: 'WIN_ON_PENALTIES',
  WIN_ON_SILVER_GOAL: 'WIN_ON_SILVER_GOAL',
  WIN_ON_TOSS_OF_COIN: 'WIN_ON_TOSS_OF_COIN',
  WIN_REGULAR: 'WIN_REGULAR',
} as const;
export type WinnerReason = Enum<typeof WinnerReason>;

export const MatchUnitSize = {
  XL: 'xl',
  L: 'l',
  L_PLUS: 'l-plus',
  M: 'm',
  S: 's',
  S_PLUS: 's-plus',
} as const;
export type MatchUnitSize = Enum<typeof MatchUnitSize>;
